import { ESCAPE } from '@angular/cdk/keycodes';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  signal,
  untracked,
  ViewEncapsulation,
} from '@angular/core';
import { outputToObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { filter } from 'rxjs/operators';

import { localStorageSignal } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import {
  TellentAssistantConfigService,
  TellentAssistantThreadsFacade,
} from '@core/tellent-assistant-logic';
import { TellentAssistantBrandMarkComponent } from '@core/tellent-assistant-ui';
import { FIXED_OVERLAY_LEVEL, OVERLAY_LEVEL, RtTooltip } from '@core/user-interface-legacy';

import { TellentAssistantPositionStrategy } from './tellent-assistant-position-strategy';
import {
  TellentAssistantChatPanelComponent,
  type TellentAssistantMode,
} from '../tellent-assistant-chat-panel/tellent-assistant-chat-panel.component';

@Component({
  selector: 'tellent-assistant',
  imports: [TellentAssistantBrandMarkComponent, RtTooltip, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation -- styles target body-attached CDK overlay host + backdrop; selectors are namespaced (tellent-assistant-overlay-panel / tellent-assistant-backdrop)
  encapsulation: ViewEncapsulation.None,
  templateUrl: './tellent-assistant.component.html',
  styleUrl: './tellent-assistant.component.less',
})
export class TellentAssistantComponent {
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly threadsFacade = inject(TellentAssistantThreadsFacade);
  private readonly config = inject(TellentAssistantConfigService);

  private readonly open = signal<boolean>(false);
  private readonly prefersFullscreen = localStorageSignal<boolean>(
    'tellent-assistant.expanded',
    false,
  );
  private readonly mode = signal<TellentAssistantMode>('compact');
  private readonly dockX = signal<number>(0);

  public readonly collapsed = input<boolean>(false);

  private overlayRef: OverlayRef | null = null;
  private panelRef: ComponentRef<TellentAssistantChatPanelComponent> | null = null;

  constructor() {
    this.mode.set(this.prefersFullscreen() ? 'fullscreen' : 'compact');
    effect(() => {
      const isOpen = this.open();
      untracked(() => (isOpen ? this.attach() : this.detach()));
    });
    effect(() => {
      const ctx = this.config.currentContext();
      const isOpen = this.open();
      if (isOpen) {
        untracked(() => {
          this.threadsFacade.loadThreads();
          this.config.loadStartersFor(ctx);
        });
      }
    });
    effect(() => {
      const mode = this.mode();
      this.applyMode(mode);
      this.panelRef?.setInput('mode', mode);
    });
    effect(() => {
      const dockOffsetX = this.dockX();
      this.panelRef?.setInput('dockOffsetX', dockOffsetX);
    });
    this.destroyRef.onDestroy(() => this.detach());
  }

  protected toggle(): void {
    this.open.update(v => !v);
  }

  private attach(): void {
    if (this.overlayRef) {
      return;
    }

    const positionStrategy = new TellentAssistantPositionStrategy();

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      panelClass: 'tellent-assistant-overlay-panel',
      backdropClass: 'tellent-assistant-backdrop',
    });

    this.overlayRef.hostElement.style.zIndex = `${FIXED_OVERLAY_LEVEL}`;

    const portalInjector = Injector.create({
      parent: this.injector,
      providers: [{ provide: OVERLAY_LEVEL, useValue: FIXED_OVERLAY_LEVEL }],
    });
    const portal = new ComponentPortal(TellentAssistantChatPanelComponent, null, portalInjector);
    this.panelRef = this.overlayRef.attach(portal);
    this.applyMode(this.mode());

    this.panelRef.setInput('mode', this.mode());
    this.panelRef.setInput('dockOffsetX', this.dockX());

    outputToObservable(this.panelRef.instance.close)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.open.set(false));

    outputToObservable(this.panelRef.instance.expand)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.prefersFullscreen.set(true);
        this.mode.set('fullscreen');
      });

    outputToObservable(this.panelRef.instance.collapse)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.prefersFullscreen.set(false);
        this.mode.set('compact');
      });

    outputToObservable(this.panelRef.instance.minimize)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.prefersFullscreen.set(false);
        this.mode.set('minimized');
      });

    outputToObservable(this.panelRef.instance.restore)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.mode.set(this.prefersFullscreen() ? 'fullscreen' : 'compact'));

    outputToObservable(this.panelRef.instance.dockOffsetXChange)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(x => this.dockX.set(x));

    this.overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.open.set(false));

    this.overlayRef
      .keydownEvents()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(e => e.keyCode === ESCAPE),
      )
      .subscribe(() => this.open.set(false));
  }

  private applyMode(mode: TellentAssistantMode): void {
    const backdrop = this.overlayRef?.backdropElement;
    if (!backdrop) return;
    const fullscreen = mode === 'fullscreen';
    backdrop.classList.toggle('expanded', fullscreen);
    backdrop.classList.toggle('compact', !fullscreen);
  }

  private detach(): void {
    if (!this.overlayRef) {
      return;
    }

    this.overlayRef.dispose();
    this.overlayRef = null;
    this.panelRef = null;
  }
}
